import { useId } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Tooltip } from "@similarweb/ui-components";

import { createDomSafeId } from "../helpers/onboardingAgentHelpers";
import type { OnboardingAgentViewportPosition } from "../OnboardingAgent/OnboardingAgent.types";
import { ONBOARDING_AGENT_TRIGGER_ARIA_LABEL, ONBOARDING_AGENT_TRIGGER_TOOLTIP } from "../texts";

import { ONBOARDING_AGENT_TOOLTIP_OPEN_DELAY_MS } from "./constants";
import { useTriggerBounceAnimation } from "./hooks/useTriggerBounceAnimation";
import { FixedSlot, IconFrame, TriggerButton } from "./styles";
import { TriggerAiBotIcon } from "./TriggerAiBotIcon";

type OnboardingAgentTriggerProps = {
    position: OnboardingAgentViewportPosition;
    shouldAnimatePosition?: boolean;
    isPanelOpen?: boolean;
    onOpen?: () => void;
};

export const OnboardingAgentTrigger = ({
    position,
    shouldAnimatePosition = false,
    isPanelOpen = false,
    onOpen,
}: OnboardingAgentTriggerProps) => {
    const { translate } = useTranslate();
    const reactId = useId();
    const gradientId = createDomSafeId("onboarding-agent-trigger-gradient", reactId);
    const isBouncing = useTriggerBounceAnimation(isPanelOpen);

    const handleClick = (): void => {
        onOpen?.();
    };

    return (
        <FixedSlot
            $x={position.x}
            $y={position.y}
            $shouldAnimatePosition={shouldAnimatePosition}
            data-automation="onboarding-agent-trigger-root"
        >
            <Tooltip
                content={translate(ONBOARDING_AGENT_TRIGGER_TOOLTIP)}
                placement="left"
                delay={{ open: ONBOARDING_AGENT_TOOLTIP_OPEN_DELAY_MS }}
            >
                <TriggerButton
                    type="button"
                    data-automation="onboarding-agent-trigger-button"
                    aria-label={translate(ONBOARDING_AGENT_TRIGGER_ARIA_LABEL)}
                    onClick={handleClick}
                    $isBouncing={isBouncing}
                >
                    <IconFrame>
                        <TriggerAiBotIcon
                            gradientId={gradientId}
                            isBouncing={isBouncing}
                        />
                    </IconFrame>
                </TriggerButton>
            </Tooltip>
        </FixedSlot>
    );
};
